/**
 * メール送付の枠（#225、docs/91 §3.1・§3.2）。帳票の紙の画面（見積書・請求書・領収書）の右の枠で「送信方法［印刷・FAX｜メール］」をメールにしたとき。
 *   useMailDelivery(target)：初めの値（POST /mail-deliveries/draft）→ この1通だけの中身（宛先・Cc・Bcc・控え Bcc・件名・本文・渡し方・公開期限・追加したファイル）
 *     → 見本（差し込みを入れた件名・本文。閲覧ページの URL は「（送信のときに作成）」）→ 送信（POST /mail-deliveries）
 *   MailPanel：送信先・件名・本文（差し込みの札を押すと入る）・渡し方（閲覧ページ｜添付）・公開期限・送付書類（同じ送付先の書類を追加・ファイルを追加）・合計の上限
 *     電子承諾の{order}（draft.ackExpiresOn がある）は渡し方が「承諾ページ｜添付」：page のボタンは承諾ページ・閲覧ページは作らない・公開期限の欄は出さない（#233 §13.7 イ）
 *   MailPreview：左の見本（差出人・件名・本文・「書類表示」のボタン）
 *   MailSendDialog：短い確認（宛先・渡し方・送付書類の数）→ 送信
 *   送付状（オンのとき）は呼ぶ側が送付状の枠（CoverLetterPanel の mail）を並べ、その中身を cover として渡す。PC だけ（PC_ONLY_FEATURES の「メールの送信」）
 */
import {
  MAIL_FIELDS,
  MAIL_FIELDS_ORDER,
  MAIL_FIELDS_NOTICE,
  MAIL_FIELDS_REVISE,
  MAIL_LIMITS,
  MAIL_MODE_LABEL,
  MAIL_MSG as M,
  MAIL_UPLOAD_ACCEPT,
  addDaysYmd,
  coverItemKey,
  coverLineText,
  encodeTitleHeader,
  fillMail,
  hasUrl,
  jpDateText,
  mailBytesLimit,
  sizeText,
  type CoverLetterContentInput,
  type DeliveryItemRef,
  type DeliveryTargetInput,
  type MailDeliveryResult,
  type MailDraft,
  type MailMode,
  type MailUpload,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ApiError, api, apiUpload, messageOf } from "../api.js";
import { t } from "../labels.js";
import { todayJst } from "../lib/num.js";
import { FileDrop } from "./FileDrop.jsx";
import { NumInput } from "./NumInput.jsx";
import { Segmented } from "./Segmented.jsx";
import { ErrorText, Field, Help, Switch } from "./ui.jsx";

import { ymdShort } from "../lib/dates.js";
export type MailForm = {
  to: string[];
  cc: string;
  bcc: string;
  ccOpen: boolean;
  bccSelf: boolean;
  subject: string;
  body: string;
  mode: MailMode;
  expiresDays: number;
  uploads: MailUpload[];
  /** 宛先を{customer}担当者のメールに登録（既定オフ） */
  saveEmail: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 「a@x.jp, b@y.jp」「a@x.jp b@y.jp」→ 配列 */
const splitAddresses = (s: string): string[] =>
  s
    .split(/[,、，\s;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export function useMailDelivery(target: DeliveryTargetInput | null) {
  const key = target ? JSON.stringify(target) : null;
  const draft = useQuery({ queryKey: ["mail-draft", key], queryFn: () => api<MailDraft>("POST", "/mail-deliveries/draft", { target }), enabled: !!target, retry: false, staleTime: 60_000, placeholderData: keepPreviousData });
  const [form, setForm] = useState<MailForm | null>(null);
  /** 中身を作った送付先（同じ送付先のあいだは書類を足しても書き直さない） */
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const d = draft.data;
    if (!d || draft.isPlaceholderData) return;
    const k = `${d.values.to}`;
    if (loadedFor.current === k) return;
    loadedFor.current = k;
    setForm({ to: d.to.length ? d.to : [""], cc: "", bcc: "", ccOpen: false, bccSelf: true, subject: d.subject, body: d.body, mode: d.mode, expiresDays: d.expiresDays, uploads: [], saveEmail: false });
  }, [draft.data, draft.isPlaceholderData]);
  const update = (p: Partial<MailForm>) => setForm((f) => (f ? { ...f, ...p } : f));
  const d = draft.data;
  const to = form ? form.to.map((x) => x.trim()).filter(Boolean) : [];
  const cc = form ? splitAddresses(form.cc) : [];
  const bcc = form ? splitAddresses(form.bcc) : [];
  const expiresYmd = form ? addDaysYmd(todayJst(), form.expiresDays) : "";
  /**
   * 電子承諾の{order}で渡し方が承諾ページ（閲覧ページを作らない＝［閲覧ページ］［期限］の行は添付と同じく消す。API と同じ）。
   * {paymentNotice}（#233 3b）で渡し方が確認ページも同じ（閲覧ページを作らない。［期限］は確認の期限＝添付でも残す）
   */
  const ack = !!(d?.ackExpiresOn || d?.confirmOn) && form?.mode === "page";
  /** 差し込みを入れた見本（URL は送信のときに作る） */
  const preview = useMemo(() => {
    if (!d || !form) return null;
    const values = {
      ...d.values,
      url: ack ? "" : M.pendingUrl,
      expires: d.confirmOn ? jpDateText(d.confirmOn) : ack ? "" : jpDateText(expiresYmd),
      ...(d.confirmOn ? { confirmUrl: M.pendingUrl } : {}),
    };
    const mode = ack ? "attach" : form.mode;
    return { subject: fillMail(form.subject, values, mode).replace(/\s+/g, " ").trim(), body: fillMail(form.body, values, mode) };
  }, [d, form, expiresYmd, ack]);
  const uploadBytes = form?.uploads.reduce((a, u) => a + u.bytes, 0) ?? 0;
  const bytes = (d?.bytes ?? 0) + uploadBytes;
  const fileCount = (d?.files.length ?? 0) + (form?.uploads.length ?? 0);
  // 誤り（送信を押せない理由。欄の下に朱の1行）
  const problems = (() => {
    if (!form || !d) return [] as string[];
    const out: string[] = [];
    if (!to.length) out.push(M.toRequired);
    if ([...to, ...cc, ...bcc].some((x) => !EMAIL_RE.test(x))) out.push(M.emailInvalid);
    if (to.length > MAIL_LIMITS.recipients || cc.length > MAIL_LIMITS.recipients || bcc.length > MAIL_LIMITS.recipients) out.push(M.tooManyAddresses);
    if (!form.subject.trim()) out.push(M.subjectRequired);
    if (!form.body.trim()) out.push(M.bodyRequired);
    if (hasUrl(form.subject) || hasUrl(form.body)) out.push(M.urlNotAllowed);
    if (form.mode === "page" && !ack && (form.expiresDays < MAIL_LIMITS.expiresMin || form.expiresDays > MAIL_LIMITS.expiresMax)) out.push(M.expiresRange);
    const limit = mailBytesLimit(form.mode);
    if (bytes > limit) out.push((form.mode === "page" ? M.overPage : M.overAttach).replace("{limit}", sizeText(limit)).replace("{size}", sizeText(bytes)));
    if (d.sentToday >= d.perDay) out.push(M.perDayLimit.replace("{n}", String(d.perDay))); // 会社の1日の通数（docs/91 §16）
    return out;
  })();
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | undefined>();
  const addFiles = async (files: File[]) => {
    setUploadErr(undefined);
    setUploading(true);
    try {
      for (const f of files) {
        if ((form?.uploads.length ?? 0) >= MAIL_LIMITS.uploads) throw new Error(M.uploadTooMany);
        if (f.size > MAIL_LIMITS.fileBytes) throw new Error(M.uploadTooBig);
        const u = await apiUpload<MailUpload>("/mail/uploads", f, f.type || "application/octet-stream", { "x-gb-title": encodeTitleHeader(f.name) });
        setForm((cur) => (cur ? { ...cur, uploads: [...cur.uploads, u] } : cur));
      }
    } catch (e) {
      setUploadErr(e instanceof ApiError || e instanceof Error ? messageOf(e) : String(e));
    } finally {
      setUploading(false);
    }
  };
  /** 送る（送付状はオンのときだけ cover） */
  const send = async (cover: CoverLetterContentInput | null): Promise<MailDeliveryResult> => {
    if (!form || !target || !d) throw new Error(M.making);
    return api<MailDeliveryResult>("POST", "/mail-deliveries", {
      target,
      mail: { to, cc, bcc, bccSelf: form.bccSelf, subject: form.subject, body: form.body, mode: form.mode, expiresDays: form.expiresDays },
      cover,
      uploads: form.uploads.map((u) => ({ id: u.id, name: u.name })),
      saveEmail: form.saveEmail && d.contact && !d.contact.email && to[0] ? { contactId: d.contact.id, email: to[0] } : null,
    });
  };
  return { draft, form, update, preview, bytes, fileCount, to, cc, bcc, expiresYmd, ack, ackExpiresOn: d?.ackExpiresOn ?? null, confirmOn: d?.confirmOn ?? null, problems, addFiles, uploading, uploadErr, send, ready: !!form && !!d };
}
export type MailDelivery = ReturnType<typeof useMailDelivery>;

/** 送付書類の操作（同じ送付先の書類＝帳票の画面で足したもの。1つ目の帳票は外せない） */
export type MailBundle = {
  /** 足した書類（上へ・下へ・削除） */
  extra: ReadonlyArray<DeliveryItemRef>;
  onMove: (i: number, dir: -1 | 1) => void;
  onRemove: (i: number) => void;
  onAddDocs: () => void;
};

export function MailPanel({ m, bundle, error, testId = "mail-panel" }: { m: MailDelivery; bundle: MailBundle; error?: string | undefined; testId?: string }) {
  const ids = useId();
  const d = m.draft.data;
  const f = m.form;
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  if (m.draft.isError) {
    return (
      <aside className="clpanel mlpanel" aria-label={M.viaMail} data-testid={testId}>
        <ErrorText>{messageOf(m.draft.error)}</ErrorText>
      </aside>
    );
  }
  if (!d || !f) {
    return (
      <aside className="clpanel mlpanel is-busy" aria-label={M.viaMail} data-testid={testId} aria-busy="true">
        <p className="hint">{M.making}</p>
      </aside>
    );
  }
  /** 差し込みの札を本文のカーソルの所へ */
  const insert = (tag: string) => {
    const el = bodyRef.current;
    const at = el ? el.selectionStart : f.body.length;
    const end = el ? el.selectionEnd : at;
    const next = f.body.slice(0, at) + tag + f.body.slice(end);
    m.update({ body: next });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + tag.length, at + tag.length);
    });
  };
  const setTo = (i: number, v: string) => m.update({ to: f.to.map((x, j) => (j === i ? v : x)) });
  const noEmail = !!d.contact && !d.contact.email;
  const limit = mailBytesLimit(f.mode);
  return (
    <aside className="clpanel mlpanel" aria-label={M.viaMail} data-testid={testId}>
      <div className="mlsec" role="group" aria-labelledby={`${ids}to`}>
        <span className="cllbl" id={`${ids}to`}>
          {M.sectionTo}
        </span>
        {f.to.map((x, i) => (
          <div className="mlrow" key={i}>
            <Field id={`${ids}t${i}`} label={i === 0 ? M.to : `${M.to} ${i + 1}`}>
              <input id={`${ids}t${i}`} className="inp" type="email" inputMode="email" autoComplete="off" list={`${ids}cand`} placeholder=" " value={x} maxLength={254} onChange={(e) => setTo(i, e.target.value.trim())} data-testid="mail-to" />
            </Field>
            {f.to.length > 1 ? (
              <button type="button" className="btn text sm" aria-label={`${M.to} ${i + 1} を${M.removeAddress}`} onClick={() => m.update({ to: f.to.filter((_, j) => j !== i) })} data-testid="mail-to-remove">
                {M.removeAddress}
              </button>
            ) : null}
          </div>
        ))}
        <datalist id={`${ids}cand`}>
          {d.candidates.map((c) => (
            <option key={c.email} value={c.email}>
              {c.label}
            </option>
          ))}
        </datalist>
        {noEmail ? <p className="hint" data-testid="mail-no-email">{t(M.noEmail)}</p> : null}
        {noEmail && m.to[0] ? (
          <label className="clcheck">
            <input type="checkbox" checked={f.saveEmail} onChange={(e) => m.update({ saveEmail: e.target.checked })} data-testid="mail-save-email" />
            <span>{t(M.registerEmail)}</span>
            <Help label={t(M.registerEmail)} text={t(M.registerEmailHelp).replace("{contact}", d.contact!.label)} testId="help-mail-save-email" />
          </label>
        ) : null}
        <div className="cladd">
          <button type="button" className="btn text" disabled={f.to.length >= MAIL_LIMITS.recipients} onClick={() => m.update({ to: [...f.to, ""] })} data-testid="mail-to-add">
            {M.addAddress}
          </button>
          <button type="button" className="btn text" aria-expanded={f.ccOpen} onClick={() => m.update({ ccOpen: !f.ccOpen })} data-testid="mail-ccbcc">
            {M.ccBcc} <span className={"vqd-chev" + (f.ccOpen ? " is-open" : "")} aria-hidden="true" />
          </button>
        </div>
        {f.ccOpen ? (
          <>
            <Field id={`${ids}cc`} label={M.cc}>
              <input id={`${ids}cc`} className="inp" inputMode="email" autoComplete="off" placeholder=" " value={f.cc} onChange={(e) => m.update({ cc: e.target.value })} data-testid="mail-cc" />
            </Field>
            <Field id={`${ids}bcc`} label={M.bcc}>
              <input id={`${ids}bcc`} className="inp" inputMode="email" autoComplete="off" placeholder=" " value={f.bcc} onChange={(e) => m.update({ bcc: e.target.value })} data-testid="mail-bcc" />
            </Field>
          </>
        ) : null}
        <div className="clrow">
          <Switch checked={f.bccSelf} onChange={(v) => m.update({ bccSelf: v })} label={M.bccSelf} testId="mail-bcc-self">
            <span>{M.bccSelf}</span>
          </Switch>
          <Help label={M.bccSelf} text={M.bccSelfHelp.replace("{email}", d.senderEmail)} testId="help-mail-bcc-self" />
        </div>
      </div>
      <div className="clbody">
        <Field id={`${ids}s`} label={M.subject}>
          <input id={`${ids}s`} className="inp" placeholder=" " value={f.subject} maxLength={MAIL_LIMITS.subject} onChange={(e) => m.update({ subject: e.target.value })} data-testid="mail-subject" />
        </Field>
        <Field id={`${ids}b`} label={M.body} help={<Help label={M.fields} text={M.fieldsHelp} testId="help-mail-fields" />}>
          <textarea id={`${ids}b`} ref={bodyRef} className="inp" rows={9} placeholder=" " value={f.body} maxLength={MAIL_LIMITS.body} onChange={(e) => m.update({ body: e.target.value })} data-testid="mail-body" />
        </Field>
        <div className="mlfields" role="group" aria-label={M.fields} data-testid="mail-fields">
          {(d.kind === "revise" ? MAIL_FIELDS_REVISE : d.kind === "order" ? MAIL_FIELDS_ORDER : d.kind === "payment_notice" ? MAIL_FIELDS_NOTICE : MAIL_FIELDS).filter((x) => d.kind !== "rfq" || (x !== "［金額］" && x !== "［お支払期限］")).map((x) => (
            <button key={x} type="button" className="btn tonal sm" onClick={() => insert(x)} data-testid="mail-field">
              {x}
            </button>
          ))}
        </div>
        <div className="clrow">
          <span className="cllbl" id={`${ids}m`}>
            {M.mode}
          </span>
          <Segmented
            options={(["page", "attach"] as const).map((v) => ({ value: v, label: v === "page" && d.confirmOn ? M.modeConfirm : v === "page" && d.ackExpiresOn ? M.modeAck : MAIL_MODE_LABEL[v], testId: `mail-mode-${v}` }))}
            value={f.mode}
            onChange={(v) => m.update({ mode: v })}
            labelledBy={`${ids}m`}
            testId="mail-mode"
          />
          <Help label={M.mode} text={d.confirmOn ? t(M.modeConfirmHelp) : d.ackExpiresOn ? t(M.modeAckHelp) : M.modeHelp} testId="help-mail-mode" />
        </div>
        {f.mode === "page" && !m.ack ? (
          <div className="clrow mlexp">
            <span className="cllbl" id={`${ids}e`}>
              {M.expires}
            </span>
            <NumInput kind="int" className="inp sm num" aria-labelledby={`${ids}e`} value={f.expiresDays} onChange={(v) => m.update({ expiresDays: v ?? MAIL_LIMITS.expiresDefault })} data-testid="mail-expires" />
            <span className="u">{M.expiresDays}</span>
            <span className="hint num" data-testid="mail-expires-until">
              {M.expiresUntil.replace("{d}", ymdShort(m.expiresYmd))}
            </span>
          </div>
        ) : null}
        <div className="cllines mlfiles" role="group" aria-label={M.files} data-testid="mail-files">
          <span className="cllbl">{M.files}</span>
          <ol>
            {d.files.map((x, i) => {
              const j = i - (d.files.length - bundle.extra.length);
              return (
                <li key={x.key} className="is-item" data-testid="mail-file">
                  <span className="no">{`${i + 1}.`}</span>
                  <span className="nm">{coverLineText(x)}</span>
                  <span className="pg num">{`${x.pages}ページ・${sizeText(x.bytes)}`}</span>
                  {j >= 0 ? (
                    <span className="ops">
                      <button type="button" className="btn text sm" disabled={j === 0} aria-label={`${coverLineText(x)}を上へ`} onClick={() => bundle.onMove(j, -1)}>
                        上へ
                      </button>
                      <button type="button" className="btn text sm" disabled={j === bundle.extra.length - 1} aria-label={`${coverLineText(x)}を下へ`} onClick={() => bundle.onMove(j, 1)}>
                        下へ
                      </button>
                      <button type="button" className="btn text sm" aria-label={`${coverLineText(x)}を削除`} onClick={() => bundle.onRemove(j)} data-testid="mail-file-remove">
                        削除
                      </button>
                    </span>
                  ) : null}
                </li>
              );
            })}
            {f.uploads.map((u, i) => (
              <li key={u.id} className="is-item" data-testid="mail-upload">
                <span className="no">{`${d.files.length + i + 1}.`}</span>
                <span className="nm">{u.name}</span>
                <span className="pg num">{sizeText(u.bytes)}</span>
                <span className="ops">
                  <button type="button" className="btn text sm" aria-label={`${u.name}を削除`} onClick={() => m.update({ uploads: f.uploads.filter((x) => x.id !== u.id) })} data-testid="mail-upload-remove">
                    削除
                  </button>
                </span>
              </li>
            ))}
          </ol>
          <div className="cladd">
            <button type="button" className="btn tonal sm" onClick={bundle.onAddDocs} data-testid="mail-add-docs">
              {M.addSameRecipient}
            </button>
          </div>
          <FileDrop
            onFiles={(fs) => void m.addFiles(fs)}
            accept={MAIL_UPLOAD_ACCEPT}
            phoneAccept={MAIL_UPLOAD_ACCEPT}
            hint={M.fileHelp}
            chooseLabel={M.addFile}
            compact
            disabled={m.uploading || f.uploads.length >= MAIL_LIMITS.uploads}
            testId="mail-drop"
            inputTestId="mail-file-input"
          />
          {m.uploading ? <p className="hint">{M.uploading}</p> : null}
          {m.uploadErr ? <ErrorText>{m.uploadErr}</ErrorText> : null}
          <p className={"hint num" + (m.bytes > limit ? " shu" : "")} data-testid="mail-total">
            {M.total.replace("{n}", String(m.fileCount)).replace("{size}", sizeText(m.bytes))}
          </p>
        </div>
        {m.problems.length ? (
          <ul className="mlproblems" data-testid="mail-problems">
            {m.problems.map((p) => (
              <li key={p} className="err">{`⚠ ${p}`}</li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <p className="err" role="alert" data-testid="mail-error">
            {`⚠ ${error}`}
          </p>
        ) : null}
      </div>
    </aside>
  );
}

/** 左の見本：差出人・件名・本文・（閲覧ページなら）「書類表示」のボタンと公開期限 */
export function MailPreview({ m, companyName }: { m: MailDelivery; companyName: string }) {
  const p = m.preview;
  const f = m.form;
  if (!p || !f) return null;
  return (
    <section className="mlpreview" aria-label={M.preview} data-testid="mail-preview">
      <div className="mlpv-head">
        <span className="chip">{M.preview}</span>
        <dl>
          <dt>{M.previewFrom}</dt>
          <dd>{companyName}</dd>
          <dt>{M.to}</dt>
          <dd className="num">{m.to.join(", ") || "—"}</dd>
          <dt>{M.previewSubject}</dt>
          <dd data-testid="mail-preview-subject">
            <b>{p.subject}</b>
          </dd>
        </dl>
      </div>
      <div className="mlpv-body" data-testid="mail-preview-body">
        {p.body}
      </div>
      {f.mode === "page" ? (
        <div className="mlpv-btn" aria-hidden="true" data-testid="mail-preview-button">
          <span>{M.mailButton}</span>
          <small>{m.ack && m.confirmOn ? M.mailConfirmExpires.replace("{d}", jpDateText(m.confirmOn)) : m.ack && m.ackExpiresOn ? M.mailAckExpires.replace("{d}", jpDateText(m.ackExpiresOn)) : M.mailExpires.replace("{d}", jpDateText(m.expiresYmd))}</small>
        </div>
      ) : null}
    </section>
  );
}

/** 送信の確認（宛先・渡し方・送付書類の数）。送れなければ窓は閉じて枠に朱の1行（呼ぶ側） */
export function MailSendDialog({ m, cover, busy, onClose, onSend }: { m: MailDelivery; cover: boolean; busy: boolean; onClose: () => void; onSend: () => void }) {
  const f = m.form!;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg mlsend" role="dialog" aria-modal="true" aria-labelledby="mlsend-h" onKeyDown={(e) => e.key === "Escape" && !busy && onClose()} data-testid="mail-send-dialog">
        <div className="dh" id="mlsend-h">
          {M.confirmTitle}
        </div>
        <div className="db">
          <dl className="rqsend-dl">
            <dt>{M.confirmTo}</dt>
            <dd data-testid="mail-confirm-to">
              <span className="num">{m.to.join(", ")}</span>
              {m.cc.length ? <span className="muted num">{`　${M.cc} ${m.cc.join(", ")}`}</span> : null}
              {m.bcc.length || f.bccSelf ? <span className="muted num">{`　${M.bcc} ${[...m.bcc, ...(f.bccSelf ? [M.bccSelf] : [])].join(", ")}`}</span> : null}
            </dd>
            <dt>{M.confirmMode}</dt>
            <dd data-testid="mail-confirm-mode">
              {f.mode !== "page"
                ? MAIL_MODE_LABEL.attach
                : m.ack && m.confirmOn
                  ? `${M.modeConfirm}（${M.confirmExpires} ${ymdShort(m.confirmOn)}）`
                  : m.ack && m.ackExpiresOn
                    ? `${M.modeAck}（${M.ackExpires} ${ymdShort(m.ackExpiresOn)} まで）`
                    : `${MAIL_MODE_LABEL.page}（${M.expires} ${ymdShort(m.expiresYmd)} まで）`}
            </dd>
            <dt>{M.confirmFiles}</dt>
            <dd data-testid="mail-confirm-files">
              {M.confirmFilesText.replace("{n}", String(m.fileCount + (cover ? 1 : 0))).replace("{size}", sizeText(m.bytes))}
              {cover ? <span className="muted">{`　${M.confirmCover}`}</span> : null}
            </dd>
          </dl>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            取消
          </button>
          <button type="button" className="btn primary" autoFocus disabled={busy} onClick={onSend} data-testid="mail-send-confirm">
            {busy ? M.sending : M.send}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 足した書類の鍵（重ねない） */
export const mailItemKeys = (items: ReadonlyArray<DeliveryItemRef>): string[] => items.map(coverItemKey);
